import { apiClient, idempotentConfig, unwrap } from './client';
import type { ApiEnvelope } from './types';

/**
 * P4 — the central alert centre (`/v1/alerts**`, OPSFL-CLIENT-PLATFORM FSD §7). STAFF plane only
 * (G25: no data-room path ever serves an alert). Keys: `alerts.read` (list/get),
 * `alerts.disposition` (workbench-side — the register links there, it does not duplicate it),
 * `alerts.rules.read` / `alerts.rules.write` (routing rules). Mutations carry `ol-idempotency-key`.
 *
 * The rules API is deliberately small (§7.4): CREATE + enable/disable exist; there is NO in-place
 * edit endpoint (correct a rule by creating its replacement and disabling the old one — the
 * "why did this fire in March" trail stays answerable) and rules are never deleted.
 */

/** Closed vocabularies from the alr-* DDL — the UI renders VERBATIM tokens, these type the filters. */
export const ALERT_STATUSES = ['OPEN', 'ROUTED', 'ACKNOWLEDGED', 'CLOSED', 'SUPPRESSED'] as const;
export const ALERT_SEVERITIES = ['HIGH', 'NORMAL', 'LOW'] as const;
export const ALERT_SOURCES = ['DERIVED', 'SIGNAL', 'SUBSCRIPTION', 'MANUAL'] as const;
export const RULE_ACTIONS = ['START_FLOW', 'SIGNAL_RUN', 'SUPPRESS'] as const;
export type RuleAction = (typeof RULE_ACTIONS)[number];

/** One durable alert row (AlertDtos.AlertView). `detail` is curated scalars only — no PII. */
export interface AlertView {
  alertId: string;
  alertKey: string;
  source: string;
  type: string;
  severity: string;
  status: string;
  entityId: string | null;
  workItemId: string | null;
  signalId: string | null;
  detail: Record<string, unknown>;
  routedRuleId: string | null;
  routedRunId: string | null;
  routedAt: string | null;
  disposition: string | null;
  dispositionNote: string | null;
  disposedBy: string | null;
  disposedAt: string | null;
  raisedAt: string | null;
}

export interface AlertListView {
  alerts: AlertView[];
  totalCount: number;
}

/** A routing rule (AlertDtos.RuleView). `matchDetail` is a FLAT equality map — §7.4, no operators. */
export interface AlertRuleView {
  ruleId: string;
  name: string;
  enabled: boolean;
  priority: number;
  matchType: string | null;
  matchSeverity: string | null;
  matchSource: string | null;
  matchDetail: Record<string, unknown> | null;
  action: string;
  flowId: string | null;
  flowVersion: number | null;
  nodeId: string | null;
  decision: string | null;
  createdBy: string | null;
  createdAt: string | null;
  updatedBy: string | null;
  updatedAt: string | null;
}

export interface AlertRuleListView {
  rules: AlertRuleView[];
  totalCount: number;
}

/** The CREATE body (AlertDtos.RuleRequest). Scalar-valued flat `matchDetail` only — the API 400s nesting. */
export interface AlertRuleRequest {
  name: string;
  priority?: number;
  matchType?: string | null;
  matchSeverity?: string | null;
  matchSource?: string | null;
  matchDetail?: Record<string, string | number | boolean> | null;
  action: RuleAction;
  flowId?: string | null;
  flowVersion?: number | null;
  nodeId?: string | null;
  decision?: string | null;
}

/**
 * Where a routing FAILURE lands (I4 — never silent): `detail.route_failure` on the alert row,
 * written by `AlertRouter` when a matched rule could not start/signal its flow. The register
 * must render this LOUD.
 */
export const ROUTE_FAILURE_KEY = 'route_failure';

export function routeFailure(alert: Pick<AlertView, 'detail'>): string | null {
  const v = alert.detail?.[ROUTE_FAILURE_KEY];
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export interface AlertQuery {
  status?: string;
  severity?: string;
  type?: string;
}

/** Normalise one wire alert: guard the detail map so `.detail[k]` never throws on a sparse row. */
function guardAlert(a: AlertView): AlertView {
  return { ...a, detail: a?.detail && typeof a.detail === 'object' ? a.detail : {} };
}

/**
 * GET /v1/alerts?status=&severity=&type= — the register, severity-first then newest, server-capped
 * at 200 rows. ACL-narrowed exactly as the workbench queue is: a confined analyst sees only alerts
 * on their allowed items (an EMPTY scope comes back as an honest empty list, never a 403).
 */
export async function listAlerts(q: AlertQuery = {}): Promise<AlertListView> {
  const { data } = await apiClient.get<ApiEnvelope<AlertListView>>('/v1/alerts', {
    params: { status: q.status, severity: q.severity, type: q.type },
  });
  const body = unwrap(data);
  const alerts = Array.isArray(body?.alerts) ? body.alerts.map(guardAlert) : [];
  return { alerts, totalCount: body?.totalCount ?? alerts.length };
}

/** GET /v1/alerts/{alertId} — one alert, routing + disposition trail included. */
export async function getAlert(alertId: string): Promise<AlertView> {
  const { data } = await apiClient.get<ApiEnvelope<AlertView>>(
    `/v1/alerts/${encodeURIComponent(alertId)}`,
  );
  return guardAlert(unwrap(data));
}

/** GET /v1/alerts/rules — every routing rule, priority order (`alerts.rules.read`). */
export async function listAlertRules(): Promise<AlertRuleListView> {
  const { data } = await apiClient.get<ApiEnvelope<AlertRuleListView>>('/v1/alerts/rules');
  const body = unwrap(data);
  const rules = Array.isArray(body?.rules) ? body.rules : [];
  return { rules, totalCount: body?.totalCount ?? rules.length };
}

/**
 * POST /v1/alerts/rules — create a rule (`alerts.rules.write`, hub_admin). The server enforces the
 * action shape (START_FLOW needs flowId; SIGNAL_RUN needs nodeId+decision) and the flat-equality
 * matchDetail rule; a named flow must be PUBLISHED or routing fails the alert loud (I4).
 */
export async function createAlertRule(request: AlertRuleRequest): Promise<AlertRuleView> {
  const { data } = await apiClient.post<ApiEnvelope<AlertRuleView>>(
    '/v1/alerts/rules',
    request,
    idempotentConfig(),
  );
  return unwrap(data);
}

/** The console test-fire request (POST /v1/alerts/test-signal). `fields` is flat scalars only. */
export interface TestSignalRequest {
  type: string;
  severity?: string | null;
  fields?: Record<string, string | number | boolean> | null;
}

/**
 * What the test fire did: the landed signal (source is ALWAYS `CONSOLE_TEST` — a test can never
 * masquerade as a real signal), the routing outcome, and the durable alert row it raised.
 */
export interface TestSignalResult {
  signalId: string;
  signalState: string;
  routingOutcome: 'ROUTED' | 'SUPPRESSED' | 'FAILED' | 'NONE' | (string & {});
  alert: AlertView;
}

/**
 * POST /v1/alerts/test-signal (`alerts.rules.write`) — inject a synthetic signal through the SAME
 * internal intake + routing path the ratified machine rail uses after authentication. Rules and
 * routing run for real; the resulting alert carries `detail.signal_source = CONSOLE_TEST`.
 * Refusals mirror the rail: 422 unknown type, 409 reserved type, 503 intake disabled.
 */
export async function sendTestSignal(request: TestSignalRequest): Promise<TestSignalResult> {
  const { data } = await apiClient.post<ApiEnvelope<TestSignalResult>>(
    '/v1/alerts/test-signal',
    request,
    idempotentConfig(),
  );
  const body = unwrap(data);
  return { ...body, alert: guardAlert(body.alert) };
}

/**
 * POST /v1/alerts/rules/{ruleId}/enable|disable — the ONLY mutation an existing rule supports
 * (no edit-in-place, no delete: disable it and create the replacement).
 */
export async function setAlertRuleEnabled(
  ruleId: string,
  enabled: boolean,
): Promise<AlertRuleView> {
  const { data } = await apiClient.post<ApiEnvelope<AlertRuleView>>(
    `/v1/alerts/rules/${encodeURIComponent(ruleId)}/${enabled ? 'enable' : 'disable'}`,
    null,
    idempotentConfig(),
  );
  return unwrap(data);
}
